import { IpRestrictionRadio } from '@/features/ip_address_whitelists/components/RestrictIpSetting/hooks/ipRestrictionRadio';
import {
  useRestrictIpSettingForm,
  type RestrictIpSettingFormValues,
} from '@/features/ip_address_whitelists/components/RestrictIpSetting/hooks/useRestrictIpSettingForm';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Caption,
  Form,
  FormRadioButton,
  ListView,
  Single,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { useWatch, type SubmitHandler } from 'react-hook-form';
import styles from './RestrictIpSetting.module.scss';

const cx = classnames.bind(styles);

const LEFT_RIGHT_RATIO = {
  left: { max: '240px', min: '240px' },
  right: {},
} as const;

export type RestrictIpSettingProps = {
  formMethods: ReturnType<typeof useRestrictIpSettingForm>;
  isIpAddressRestrictionEnabled: boolean;
  isEditing: boolean;
  isWhitelistEmpty?: boolean;
  isHeaderActionDisabled?: boolean;
  onClickEdit: () => void;
  onClickCancel: () => void;
  onSave: SubmitHandler<RestrictIpSettingFormValues>;
};

export const RestrictIpSetting: FC<RestrictIpSettingProps> = memo(
  ({
    formMethods,
    isIpAddressRestrictionEnabled,
    isEditing,
    isWhitelistEmpty = false,
    isHeaderActionDisabled = false,
    onClickEdit,
    onClickCancel,
    onSave,
  }) => {
    const { t } = useTranslation();
    const {
      control,
      formState: { isSubmitting },
    } = formMethods;

    const ipRestrictionFieldName = 'ip_address_restriction_enabled';
    const selectedIpRestriction = useWatch({
      control,
      name: ipRestrictionFieldName,
    });
    const cannotEnableRestriction =
      isWhitelistEmpty &&
      !isIpAddressRestrictionEnabled &&
      selectedIpRestriction === IpRestrictionRadio.Enabled;
    const radioOptions = useMemo(
      () => [
        {
          id: IpRestrictionRadio.Enabled,
          value: IpRestrictionRadio.Enabled,
          label: t('ip_address_whitelist_restrict_enabled'),
        },
        {
          id: IpRestrictionRadio.Disabled,
          value: IpRestrictionRadio.Disabled,
          label: t('ip_address_whitelist_restrict_disabled'),
        },
      ],
      [t]
    );

    return (
      <section className={cx('section')}>
        <div
          className={cx(
            'heading',
            isWhitelistEmpty ? '' : 'heading-no-description'
          )}
        >
          <div className={cx('heading-text')}>
            <h2 className={cx('title')}>
              {t('ip_address_whitelist_restrict_setting_title')}
            </h2>
            {isWhitelistEmpty ? (
              <p className={cx('heading-description')}>
                {t('ip_address_whitelist_restrict_heading_description')}
              </p>
            ) : null}
          </div>
          {isEditing ? (
            <ButtonGroup direction='horizontal'>
              <ButtonV2
                size='sm'
                isSecondary
                onClick={onClickCancel}
                isDisabled={isSubmitting}
              >
                {t('cancel')}
              </ButtonV2>
              <ButtonV2
                size='sm'
                onClick={formMethods.handleSubmit(onSave)}
                isDisabled={isSubmitting || cannotEnableRestriction}
                isLoading={isSubmitting}
              >
                {t('save')}
              </ButtonV2>
            </ButtonGroup>
          ) : (
            <ButtonV2
              size='sm'
              isSecondary
              onClick={onClickEdit}
              isDisabled={isHeaderActionDisabled}
            >
              {t('edit')}
            </ButtonV2>
          )}
        </div>

        <ListView leftRightRatio={LEFT_RIGHT_RATIO}>
          <Single>
            <div className={cx('label')}>
              {t('ip_address_whitelist_restrict_label')}
            </div>
            {isEditing ? (
              <div className={cx('edit-area')}>
                <Form<RestrictIpSettingFormValues>
                  formMethod={formMethods}
                  onSubmit={onSave}
                  name='ip_address_restriction_setting'
                >
                  <FormRadioButton<RestrictIpSettingFormValues>
                    options={radioOptions}
                    control={control}
                    name={ipRestrictionFieldName}
                    inputId={ipRestrictionFieldName}
                    testId={ipRestrictionFieldName}
                  />
                </Form>
                {cannotEnableRestriction ? (
                  <Caption
                    type='error'
                    testId='ip_address_restriction_no_whitelist_error'
                  >
                    {t('ip_address_whitelist_restrict_validation_no_whitelist')}
                  </Caption>
                ) : null}
                <p className={cx('description')}>
                  {t('ip_address_whitelist_restrict_description')}
                </p>
              </div>
            ) : (
              <div className={cx('restrict-value')}>
                <span className={cx('value-text')}>
                  {isIpAddressRestrictionEnabled
                    ? t('ip_address_whitelist_restrict_enabled')
                    : t('ip_address_whitelist_restrict_disabled')}
                </span>
              </div>
            )}
          </Single>
        </ListView>
      </section>
    );
  }
);
RestrictIpSetting.displayName = 'RestrictIpSetting';
